import { color } from './primitives';

/**
 * Semantic alias layer — Figma's "Semantic" collection (Mode 1), every value
 * a reference into `primitives`. The `palette.ts` and `components.ts` files
 * consume tokens from here (and from primitives directly only for values
 * Figma's semantic layer doesn't yet name).
 *
 * Adding a new semantic key here is the right move when a component would
 * otherwise reach for a raw primitive in `components.ts`.
 */
export const semantic = {
  /** Page / surface backgrounds. */
  bg: {
    /** Default page background (Figma BG/BG-Primary). */
    primary: color.neutrals[50],
    /** Inverse surface — sidebar, dark nav (Figma BG/BG-Secondary). */
    secondary: color.vayuBlue[950],
    /** Card surface — paper white (Figma BG/Card/card-bg-primary). */
    cardPrimary: color.base.white,
    /** Card surface — neutral tinted (Figma BG/Card/card-bg-secondary). */
    cardSecondary: color.neutrals[50],
    /**
     * Subtle inset surface — small chips/tiles inside a white card
     * (Figma BG/Subtle). E.g. the icon tile in VyCard.
     */
    subtle: color.neutrals[50],
    /**
     * Nested card surface — the darker inset card painted *inside* a
     * white card (Figma `#FAFBFE`). E.g. the body slot in `VyToggleSection`
     * or the Installments Breakdown panel.
     */
    cardInset: color.neutrals[25],
    /**
     * Usage breakdown panel background — the tinted surface behind the
     * usage summary rows and chart area (`#F5F6FA`).
     */
    usagePanel: color.neutrals[50],
  },

  /** Button state colors per variant. */
  button: {
    primary: {
      idle: color.vayuBlue[950],
      hover: color.vayuBlue[900],
      focused: color.vayuBlue[500],
      disabled: color.vayuBlue[200],
    },
    secondary: {
      idle: color.neutrals[100],
      hover: color.neutrals[200],
      focused: color.neutrals[400],
      disabled: color.neutrals[50],
    },
    text: {
      idle: color.base.white,
      hover: color.neutrals[50],
      focused: color.neutrals[100],
    },
  },

  /** Tag (chip) bg + text pairs per status. */
  tags: {
    alert: { bg: color.alert[50], text: color.alert[900] },
    info: { bg: color.vayuBlue[50], text: color.vayuBlue[500] },
    success: { bg: color.success.badgeBg, text: color.success.badgeText },
  },

  /** Inline callout (info banner) bg + text pairs. */
  callout: {
    info: { bg: color.vayuBlue.actionSubtle, text: color.vayuBlue.action },
  },

  /** Text colors. */
  text: {
    primary: color.neutrals[850],
    secondary: color.neutrals[550],
    /** Muted label text — e.g. usage segment name in breakdown rows. */
    tertiary: color.neutrals[600],
    /** Body text for usage row values and sub-headings. */
    body: color.neutrals[650],
    /** Strong value text — e.g. usage count totals. */
    strong: color.neutrals[725],
    /** Near-black heading text — e.g. usage modal title. */
    heading: color.neutrals[825],
    /** Overage state text — signals exceeded allocation. */
    overage: color.alert[950],
  },

  /** Divider colors. */
  divider: {
    default: color.neutrals[200],
    /** Subtle hairline — e.g. the drawer header border (Figma grey/95 #F0F0F5). */
    subtle: color.neutrals[75],
  },

  /** Focus / accent ring colors. */
  accent: {
    focus: color.vayuBlue.accentFocus,
  },

  /** Dropdown menu item state colors. */
  dropdown: {
    menuItem: {
      primary: color.base.white,
      hover: color.neutrals[50],
      focused: color.neutrals[100],
    },
  },

  /**
   * Filter button / popover accent colors.
   * `accent` replaces #5083FF (vayuBlue[400]).
   * `activeBg` replaces #E7F2FF (vayuBlue[50]).
   * `activeHoverBg` replaces #D4E7FF (vayuBlue[100]).
   * `popoverBorder` replaces #F0F4F6 (neutrals[75]).
   * `popoverShadow` — popover box-shadow.
   * `idleBg` replaces #F5F6FA (neutrals[50]).
   * `addButtonBg` replaces #E8EBF2 (neutrals[100]).
   * `addButtonHoverBg` replaces #E0E0E0 (neutrals[200]).
   */
  filter: {
    accent: color.vayuBlue[400],
    activeBg: color.vayuBlue[50],
    activeHoverBg: color.vayuBlue[100],
    /** Popover/menu border (replaces #F0F4F6). */
    popoverBorder: color.neutrals[75],
    /** Popover box-shadow. */
    popoverShadow: '0px 8px 16px 0px rgba(12, 18, 69, 0.08)',
    /** Idle filter button background (replaces #F5F6FA). */
    idleBg: color.neutrals[50],
    /** "Add filter" button background (replaces #E8EBF2). */
    addButtonBg: color.neutrals[100],
    /** "Add filter" button hover background (replaces #E0E0E0). */
    addButtonHoverBg: color.neutrals[200],
  },

  /**
   * Elevation shadows from Figma's Effect styles. Both surface and hero
   * treatments share these — keeping them here means the soft-floating
   * feel is consistent across `VyCard`, `VyHeroIcon`, and any future
   * primitive that wants the same elevation language.
   */
  shadow: {
    /** Shadow/Base — default resting elevation (cards, hero icon). */
    base: '0px 1px 2px rgba(0, 0, 0, 0.04), 0px 1px 3px rgba(0, 0, 0, 0.08)',
    /** Shadow/MD — hover/active elevation (deeper lift). */
    md: '0px 2px 4px -1px rgba(0, 0, 0, 0.06), 0px 4px 6px -1px rgba(0, 0, 0, 0.10)',
  },

  /** Border colors for usage breakdown surfaces. */
  border: {
    /** Grid-line / panel border — usage chart gridlines and card borders. */
    gridLine: color.neutrals[100],
    /** Swatch border — the stroke around a color swatch in the chart legend. */
    swatch: color.neutrals[300],
  },

  /** Progress bar / linear tracker colors. */
  progress: {
    /** Fill color for the usage progress bar (within-allocation state). */
    fill: color.vayuBlue[400],
    /** Fill color for the progress bar when allocation is exceeded. */
    overageFill: color.alert[700],
  },

  /** Overage / exceeded-allocation visual tokens. */
  overage: {
    /** Text color for overage labels and amounts. */
    text: color.alert[950],
    /** Fill / highlight color for the overage region in the chart. */
    fill: color.alert[700],
  },
} as const;
